import { Platform, Text, View } from "react-native";
import {
  ArrowRight,
  CheckCircle2,
  Lock,
  type LucideIcon,
} from "lucide-react-native";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { CardSurface, CompactActionButton, Tag } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";
export type ClinicShortlistCriterion = {
  id: string;
  label: string;
  done: boolean;
  icon: LucideIcon;
  doneLabel?: string;
  missingLabel?: string;
};

export type ClinicShortlistMatch = {
  id: string;
  name: string;
  tag: string;
  distance?: string;
  why: string;
  estimate?: string;
};

type Props = {
  criteria: ClinicShortlistCriterion[];
  matches?: ClinicShortlistMatch[];
  title?: string;
  lockedDescription: string;
  emptyDescription: string;
  resolveAction: (criteria: ClinicShortlistCriterion[]) => PlanAction;
  onAction: (action: PlanAction) => void;
};

type ClinicShortlistCarouselItem =
  ClinicShortlistMatch | { id: string; placeholder: true };

function CriterionPill({ criterion }: { criterion: ClinicShortlistCriterion }) {
  const t = useTranslations();
  const label = criterion.done
    ? (criterion.doneLabel ??
      t("homepage.clinic_shortlist_section.criterion_added", {
        params: { label: criterion.label },
      }))
    : (criterion.missingLabel ??
      t("homepage.clinic_shortlist_section.criterion_add", {
        params: { label: criterion.label.toLowerCase() },
      }));

  // Added criteria read as filled; the ones still missing as outlines.
  return (
    <Tag
      label={label}
      variant={criterion.done ? "soft" : "outline"}
      icon={criterion.done ? CheckCircle2 : criterion.icon}
    />
  );
}

function PlaceholderMatchCard({ width }: { width: number }) {
  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: 218,
        padding: 14,
        opacity: 0.42,
      }}
    >
      <View
        style={{
          width: 112,
          height: 22,
          borderRadius: 999,
          backgroundColor: "rgba(92, 52, 32, 0.1)",
        }}
      />
      <View
        style={{
          width: "72%",
          height: 20,
          borderRadius: 8,
          backgroundColor: "rgba(92, 52, 32, 0.14)",
          marginTop: 18,
        }}
      />
      <View
        style={{
          width: 52,
          height: 14,
          borderRadius: 7,
          backgroundColor: "rgba(92, 52, 32, 0.1)",
          marginTop: 8,
        }}
      />
      <View
        style={{
          gap: 7,
          marginTop: 20,
        }}
      >
        {[0, 1, 2].map((item) => (
          <View
            key={item}
            style={{
              width: `${92 - item * 14}%`,
              height: 10,
              borderRadius: 5,
              backgroundColor: "rgba(92, 52, 32, 0.1)",
            }}
          />
        ))}
      </View>
    </CardSurface>
  );
}

function ClinicMatchCard({
  clinic,
  width,
  locked,
}: {
  clinic: ClinicShortlistMatch;
  width: number;
  locked: boolean;
}) {
  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: 218,
        padding: 14,
        opacity: locked ? 0.45 : 1,
        ...(Platform.OS === "web" && locked
          ? ({ filter: "blur(2px)" } as Record<string, string>)
          : null),
      }}
    >
      <Tag label={clinic.tag} style={{ alignSelf: "flex-start" }} />

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 16,
          lineHeight: 21,
          color: EMBER.colors.text,
          fontWeight: "700",
          marginTop: 10,
        }}
      >
        {clinic.name}
      </Text>
      {clinic.distance ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.textLight,
            fontWeight: "700",
            marginTop: 3,
            textTransform: "uppercase",
            letterSpacing: 0.35,
          }}
        >
          {clinic.distance}
        </Text>
      ) : null}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 10,
        }}
      >
        {clinic.why}
      </Text>
      {clinic.estimate ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textLight,
            fontWeight: "700",
            marginTop: "auto",
            paddingTop: 12,
          }}
        >
          {clinic.estimate}
        </Text>
      ) : null}
    </CardSurface>
  );
}

export function ClinicShortlistSection({
  criteria,
  matches = [],
  title,
  lockedDescription,
  emptyDescription,
  resolveAction,
  onAction,
}: Props) {
  const t = useTranslations();
  const resolvedTitle =
    title ?? t("homepage.clinic_shortlist_section.unlock_shortlist");
  const locked = criteria.some((criterion) => !criterion.done);
  const matchingAction = resolveAction(criteria);
  const carouselItems: ClinicShortlistCarouselItem[] =
    matches.length > 0
      ? matches
      : [
          { id: "placeholder-1", placeholder: true },
          { id: "placeholder-2", placeholder: true },
        ];

  return (
    <View style={{ borderRadius: 16, overflow: "hidden" }}>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 7,
          marginBottom: 12,
        }}
      >
        {criteria.map((criterion) => (
          <CriterionPill key={criterion.id} criterion={criterion} />
        ))}
      </View>

      <View>
        <HorizontalSnapCarousel
          items={carouselItems}
          gap={10}
          fallbackCardWidth={244}
          getCardWidth={(containerWidth) =>
            Math.min(268, Math.max(232, containerWidth * 0.82))
          }
          keyExtractor={(item) => item.id}
          renderItem={(item, width) =>
            "placeholder" in item ? (
              <PlaceholderMatchCard width={width} />
            ) : (
              <ClinicMatchCard clinic={item} width={width} locked={locked} />
            )
          }
          contentContainerStyle={{ paddingRight: 2 }}
        />

        {locked || matches.length === 0 ? (
          <View
            pointerEvents="box-none"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              alignItems: "center",
              justifyContent: "center",
              padding: 18,
            }}
          >
            <View
              style={{
                width: "100%",
                borderRadius: 16,
                borderWidth: 1,
                borderColor: semanticColors.border.glass,
                backgroundColor: "rgba(255,255,255,0.72)",
                padding: 14,
                ...(Platform.OS === "web"
                  ? ({
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    } as Record<string, string>)
                  : null),
              }}
            >
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
              >
                <Lock size={15} color={EMBER.colors.text} strokeWidth={1.8} />
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 1,
                    fontSize: 14,
                    lineHeight: 18,
                    color: EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {locked
                    ? resolvedTitle
                    : t("homepage.clinic_shortlist_section.not_ready_yet")}
                </Text>
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 18,
                  color: EMBER.colors.textMid,
                  fontWeight: "400",
                  marginTop: 7,
                }}
              >
                {locked ? lockedDescription : emptyDescription}
              </Text>
              <CompactActionButton
                label={matchingAction.label}
                action="clinic_shortlist_cta"
                variant="primary"
                size="sm"
                trailingIcon={ArrowRight}
                onPress={() => onAction(matchingAction)}
                style={{ alignSelf: "flex-start", marginTop: 11 }}
              />
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}
